import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiFolder, FiFile, FiTrash2, FiEdit, FiUploadCloud, FiRefreshCw, FiSearch, FiCheckCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../config";
import {
    Button,
    Card,
    PageHeader,
    Modal,
    EmptyState,
    Tooltip,
    useConfirm,
    usePrompt,
    formatBytes,
    formatDateTime,
} from "./ui";
import { TableSkeleton } from "./ui/Skeleton";

export default function FileManager() {
    const confirm = useConfirm();
    const prompt = usePrompt();

    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('main'); // 'main' or 'update'
    const [search, setSearch] = useState("");
    const [uploadOpen, setUploadOpen] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [progress, setProgress] = useState(0);
    const fileInputRef = useRef(null);

    const fetchFiles = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/files?type=${activeTab}`);
            const data = await res.json();
            if (data.success) {
                setFiles(data.files);
            } else {
                toast.error("Gagal memuat file: " + data.error);
            }
        } catch (error) {
            toast.error("Error: " + error.message);
        }
        setLoading(false);
    }, [activeTab]);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    /**
     * Chunked upload — logic identical to the previous implementation
     * (500KB chunks, same endpoint/field names), plus a visible progress bar.
     */
    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setProgress(0);
        const toastId = toast.loading("Preparing upload...");

        const CHUNK_SIZE = 500 * 1024; // 500KB chunk
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        try {
            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(file.size, start + CHUNK_SIZE);
                const chunk = file.slice(start, end);

                const formData = new FormData();
                formData.append("file", chunk);
                formData.append("filename", file.name);
                formData.append("chunkIndex", i);
                formData.append("totalChunks", totalChunks);
                formData.append("type", activeTab);

                const percent = Math.round(((i + 1) / totalChunks) * 100);
                setProgress(percent);
                toast.update(toastId, { render: `Uploading... ${percent}%`, type: "info", isLoading: true });

                const res = await fetch(`${API_BASE_URL}/admin/files/upload_chunk`, {
                    method: "POST",
                    body: formData,
                });

                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(`Server returned ${res.status}: ${text}`);
                }

                const data = await res.json();
                if (!data.success) {
                    throw new Error(data.error);
                }
            }

            toast.update(toastId, { render: "✅ Upload complete!", type: "success", isLoading: false, autoClose: 3000 });
            fetchFiles();
            setUploadOpen(false);
        } catch (error) {
            console.error("Upload error:", error);
            let errMsg = error.message;
            if (errMsg.includes("<") || errMsg.includes("Unexpected token")) {
                errMsg = "Server returned an error (likely limit exceeded or timeout).";
            }
            toast.update(toastId, { render: "❌ Upload failed: " + errMsg, type: "error", isLoading: false, autoClose: 5000 });
        }

        setUploading(false);
        setProgress(0);
        e.target.value = null; // reset input
    };

    const handleDelete = async (filename) => {
        const ok = await confirm({
            title: "Hapus File",
            message: `Yakin ingin menghapus file "${filename}"?`,
            confirmLabel: "Ya, Hapus",
            tone: "danger",
        });
        if (!ok) return;

        try {
            const res = await fetch(`${API_BASE_URL}/admin/files/delete`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ filename, type: activeTab }),
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                fetchFiles();
            } else {
                toast.error("Gagal hapus: " + data.error);
            }
        } catch (error) {
            toast.error("Error: " + error.message);
        }
    };

    const handleRename = async (oldName) => {
        const newName = await prompt({
            title: "Ubah Nama File",
            label: "Nama baru",
            defaultValue: oldName,
        });
        if (!newName || newName === oldName) return;

        try {
            const res = await fetch(`${API_BASE_URL}/admin/files/rename`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ old_name: oldName, new_name: newName, type: activeTab }),
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                fetchFiles();
            } else {
                toast.error("Gagal rename: " + data.error);
            }
        } catch (error) {
            toast.error("Error: " + error.message);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        if (fileInputRef.current) {
            const dt = new DataTransfer();
            dt.items.add(file);
            fileInputRef.current.files = dt.files;
            handleUpload({ target: fileInputRef.current });
        }
    };

    const filteredFiles = files.filter((f) => f.name.toLowerCase().includes(search.toLowerCase()));

    const tabs = [
        { id: "main", label: "File Utama" },
        { id: "update", label: "Program Update" },
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            <PageHeader
                title="File Manager"
                subtitle="Kelola berkas sistem dan program update"
                icon={FiFolder}
                actions={
                    <>
                        <Tooltip label="Muat ulang">
                            <Button variant="secondary" size="icon" onClick={fetchFiles} aria-label="Muat ulang">
                                <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                            </Button>
                        </Tooltip>
                        <Button icon={FiUploadCloud} onClick={() => setUploadOpen(true)}>
                            Upload File
                        </Button>
                    </>
                }
            />

            <Card>
                {/* Tabs + search */}
                <div className="flex flex-col gap-3 border-b border-slate-100 p-4 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between">
                    <div className="inline-flex rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                                    activeTab === tab.id
                                        ? "bg-white text-primary-600 shadow-soft dark:bg-slate-900 dark:text-primary-400"
                                        : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full lg:max-w-xs">
                        <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Cari file..."
                            className="field-base pl-10"
                        />
                    </div>
                </div>

                {/* File list */}
                {loading ? (
                    <TableSkeleton rows={6} columns={3} />
                ) : filteredFiles.length === 0 ? (
                    <EmptyState
                        icon={FiFolder}
                        title={search ? "File tidak ditemukan" : "Belum ada file"}
                        description={search ? "Coba kata kunci lain." : "Upload file pertama Anda untuk mulai."}
                        action={
                            !search && (
                                <Button icon={FiUploadCloud} onClick={() => setUploadOpen(true)}>
                                    Upload File
                                </Button>
                            )
                        }
                    />
                ) : (
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                        {filteredFiles.map((file) => (
                            <li
                                key={file.name}
                                className="flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                            >
                                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
                                    <FiFile className="h-5 w-5" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{file.name}</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        {formatBytes(file.size)} · {formatDateTime(new Date(file.mtime * 1000))}
                                    </p>
                                </div>
                                <div className="flex flex-shrink-0 items-center gap-1">
                                    <Tooltip label="Ubah nama">
                                        <Button variant="ghost" size="icon" onClick={() => handleRename(file.name)} aria-label={`Ubah nama ${file.name}`}>
                                            <FiEdit className="h-4 w-4" />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip label="Hapus">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(file.name)}
                                            className="text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10"
                                            aria-label={`Hapus ${file.name}`}
                                        >
                                            <FiTrash2 className="h-4 w-4" />
                                        </Button>
                                    </Tooltip>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            {/* Upload modal */}
            <Modal
                open={uploadOpen}
                onClose={() => !uploading && setUploadOpen(false)}
                title="Upload File"
                subtitle={`Tujuan: ${activeTab === "main" ? "File Utama" : "Program Update"}`}
                closeOnBackdrop={!uploading}
            >
                <div
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                    }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleDrop}
                    className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
                        dragActive
                            ? "border-primary-400 bg-primary-50/60 dark:bg-primary-500/10"
                            : "border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-800/40"
                    }`}
                >
                    <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
                        <FiUploadCloud className="h-7 w-7" />
                    </span>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Drag &amp; Drop file di sini</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">atau pilih file dari komputer Anda</p>

                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} disabled={uploading} />

                    <Button
                        className="mt-5"
                        variant="secondary"
                        icon={FiUploadCloud}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                    >
                        Pilih File
                    </Button>
                </div>

                {uploading && (
                    <div className="mt-5">
                        <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-slate-600 dark:text-slate-300">
                            <span>Mengunggah...</span>
                            <span>{progress}%</span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-200"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                )}

                {!uploading && (
                    <p className="mt-4 flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
                        <FiCheckCircle className="h-3.5 w-3.5" />
                        Upload dilakukan per 500KB agar mendukung file besar.
                    </p>
                )}
            </Modal>
        </div>
    );
}
